<template>
  <div class="transaction-report-view">
    <div class="main-content">
      <ErrorMessage v-if="errorMessage" :message="errorMessage" type="danger" />

      <h2 class="section-title">
        {{ t("CL_TRANSACTIONS.CHOOSE_ISSUE_TITLE") }}
      </h2>

      <div class="report-options">
        <RadioButtonCard
          v-for="option in reportOptions"
          :key="option.id"
          :id="option.id"
          :text="option.title"
          :description="option.description"
          :value="option.id"
          :isActive="selectedReason === option.id"
          @onchange="selectReason"
        >
          <InputFieldText
            v-if="option.id === 'OTHER' && selectedReason === 'OTHER'"
            v-model="otherReasonText"
            :placeholder="t('CL_TRANSACTIONS.OTHER_REASON_PLACEHOLDER')"
            input-id="other-reason-input"
            :required="true"
            :maxlength="993"
            :regex="allowedCharsRegex"
          />
        </RadioButtonCard>
      </div>
    </div>

    <div class="d-flex">
      <Button
        id="submit-report-btn"
        :disabled="!isSubmitEnabled"
        @click="emitSubmit"
      >
        {{ t("CL_TRANSACTIONS.SUBMIT_REPORT") }}
      </Button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import RadioButtonCard from "@/apps/credit-line/components/RadioButtonCard.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import InputFieldText from "@/shared-components/input-fields/InputFieldText.vue";
import ErrorMessage from "@/apps/dashboard/components/common/ErrorMessage.vue";

const { t } = useI18n();

// Log the props received
const props = defineProps<{
  debitId: number | string; // Accept both number and string types
  errorMessage?: string | undefined;
  disableButton?: boolean;
}>();

const emit = defineEmits<{
  (e: "submit", reason: string, otherReason: string): void;
}>();

const selectedReason = ref("");
const otherReasonText = ref("");
const reportOptions = [
  {
    id: "NOT_MY_TRANSACTION",
    title: t("CL_TRANSACTIONS.NOT_MY_TRANSACTION"),
    description: t("CL_TRANSACTIONS.NOT_MY_TRANSACTION_DESC"),
  },
  {
    id: "CHARGED_TWICE",
    title: t("CL_TRANSACTIONS.CHARGED_TWICE"),
    description: t("CL_TRANSACTIONS.CHARGED_TWICE_DESC"),
  },
  {
    id: "PROBLEM_WITH_STORE",
    title: t("CL_TRANSACTIONS.PROBLEM_WITH_STORE"),
    description: t("CL_TRANSACTIONS.PROBLEM_WITH_STORE_DESC"),
  },
  {
    id: "OTHER",
    title: t("CL_TRANSACTIONS.OTHER_PROBLEM"),
  },
];

/* Log the options to ensure they're properly initialized
console.log(
  "Available report options:",
  reportOptions.map((o) => o.id)
);*/

const selectReason = (reason: string) => {
  selectedReason.value = reason;
};

const isSubmitEnabled = computed(() => {
  if (selectedReason.value === "OTHER") {
    return otherReasonText.value.trim().length > 0;
  }
  if (props.disableButton) {
    return false;
  }
  return selectedReason.value !== "";
});

const emitSubmit = () => {
  emit("submit", selectedReason.value, otherReasonText.value ?? "");
};

const allowedCharsRegex = "[a-zA-Z0-9\\u00C0-\\u024F\\s.@,!?;:\"'()\\-_+]";
</script>

<style scoped lang="scss">
.transaction-report-view {
  flex-direction: column;
  height: 100vh;
  background-color: white;
  z-index: 10;
  position: relative;
  -webkit-overflow-scrolling: touch; /* Improves scrolling on iOS */
  font-family: Inter;
}

.main-content {
  flex-grow: 1;
  padding: 20px;
  overflow-y: auto;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
}

.report-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;

  /* Enhanced styling for radio button cards */
  :deep(.d-flex) {
    background-color: white !important;
    border: 1px solid #d9d7dd !important;
    border-radius: 5px !important;
    padding: 16px !important;
    margin-bottom: 12px !important;
    display: flex !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    position: relative !important;
    z-index: 5 !important;
  }

  :deep(.active) {
    border: 1px solid #28a745 !important;
    background-color: #e6f4ea !important;
  }

  :deep(.radio) {
    min-width: 24px !important;
    height: 24px !important;
    margin-right: 12px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
    z-index: 10 !important;
  }

  :deep(.radio span) {
    display: inline-block !important;
    width: 20px !important;
    height: 20px !important;
    border-radius: 50% !important;
    border: 2px solid #28a745 !important;
    position: relative !important;
    visibility: visible !important;
    z-index: 11 !important;
  }

  :deep(.radio span::after) {
    content: "" !important;
    width: 12px !important;
    height: 12px !important;
    background: #28a745 !important;
    position: absolute !important;
    border-radius: 50% !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    opacity: 0 !important;
    transition: opacity 0.2s !important;
    z-index: 12 !important;
  }

  :deep(.active .radio span::after) {
    opacity: 1 !important;
  }

  :deep(.text),
  :deep(.description) {
    color: #333 !important;
    font-size: 16px !important;
    line-height: 24px !important;
    visibility: visible !important;
    z-index: 5 !important;
  }

  :deep(.description) {
    font-size: 14px !important;
    color: #666 !important;
    line-height: 20px !important;
    margin-top: 4px !important;
  }

  /* Better styling for the input field */
  :deep(.field) {
    margin-top: 12px !important;
    margin-left: 36px !important; /* Align with text content */
    width: calc(100% - 36px) !important;
  }

  :deep(input) {
    border: 1px solid #d9d7dd !important;
    border-radius: 4px !important;
    padding: 10px !important;
    width: 100% !important;
    font-size: 14px !important;
  }

  :deep(input:focus) {
    border-color: #28a745 !important;
    outline: none !important;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.25) !important;
  }

  /* Additional WebView-specific fixes */
  /* Target WebView Chrome */
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    :deep(.radio) {
      -webkit-appearance: none !important;
      appearance: none !important;
      -webkit-tap-highlight-color: transparent !important;
    }

    :deep(input[type="radio"]) {
      opacity: 1 !important;
      pointer-events: auto !important;
      position: static !important;
      visibility: visible !important;
    }
  }
}

#submit-report-btn {
  margin: 20px auto;
  padding: 12px;
  font-size: 16px;
  font-weight: bold;
  width: 90%;
}
</style>
